import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Grid, 
  List, 
  Download, 
  Star, 
  Eye,
  MoreVertical,
  Plus,
  RefreshCw
} from 'lucide-react';
import { TemplatePreview } from './components/TemplatePreview';
import { TemplateModal } from './components/TemplateModal';
import { useTemplateApi } from './hooks/useTemplateApi';
import { useAutoTemplateSync } from './hooks/useAutoTemplateSync';

interface TemplatesManagementProps {
  onNavigateToTemplateManager: () => void;
}

// Sample user data for template previews
const sampleUserData = {
  personal_info: {
    name: "John Doe",
    firstName: "John",
    lastName: "Doe",
    profession: "Software Engineer",
    email: "john.doe@example.com",
    phone: "+33 6 12 34 56 78",
    fullAddress: "Paris, France"
  },
  profil: {
    description: "Experienced software engineer with a passion for creating innovative solutions."
  },
  summary: "Software engineer with 5+ years of experience in web development.",
  formations: [
    {
      diploma: "Master in Computer Science",
      school: "University of Technology",
      location: "Paris",
      startDate: "2018-09-01",
      endDate: "2020-06-30",
      description: "Specialized in software engineering and AI."
    }
  ],
  professional_experiences: [
    {
      title: "Senior Software Engineer",
      company: "Tech Company",
      startDate: "2020-07-01",
      endDate: "2023-12-31",
      isCurrent: false,
      description: "Led development of web applications using React and Node.js."
    }
  ],
  skills: [
    {
      category: "Programming",
      items: ["JavaScript", "Python", "Java"],
      levels: { "JavaScript": 5, "Python": 4, "Java": 3 }
    }
  ],
  languages: [
    { name: "English", level: "Fluent" },
    { name: "French", level: "Native" }
  ],
  interests: ["Travel", "Photography", "Technology"],
  qualities: ["Creative", "Detail-oriented", "Team player"],
  certificates: [
    {
      title: "AWS Certified Developer",
      organization: "Amazon Web Services",
      date: "2022-05-15",
      description: "Associate level certification for cloud development"
    }
  ],
  references: [
    {
      name: "Jane Smith",
      role: "Engineering Manager",
      company: "Previous Company",
      email: "jane.smith@example.com",
      phone: "+33 6 98 76 54 32"
    }
  ],
  theme_colors: {
    RightSectionColor: "#ea580c",
    BgColor: "#f3f4f6",
    RightTextColor: "#111827",
    LeftTextColor: "#6b7280"
  }
};

export const TemplatesManagement = ({ onNavigateToTemplateManager }: TemplatesManagementProps) => {
  // TOUS LES HOOKS DOIVENT ÊTRE APPELÉS EN PREMIER (avant tout useEffect ou condition)
  // API réelle au lieu des données mockées
  const { templates = [], loading, refreshTemplates } = useTemplateApi();
  const { 
    detectedTemplates, 
    isDetecting, 
    lastSyncTime, 
    syncStatus, 
    autoSync,
    scanTemplates 
  } = useAutoTemplateSync();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters, setFilters] = useState({
    category: '',
    industry: '',
    search: '',
    sort: 'newest'
  });
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Charger les templates au montage du composant (useEffect après tous les hooks)
  useEffect(() => {
    refreshTemplates();
  }, [refreshTemplates]);

  // Fonctions
  const handleViewTemplate = (template: any) => {
    setSelectedTemplate(template);
    setIsModalOpen(true);
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  // Données maintenant fournies par l'API réelle via useTemplateApi

  const handleSearch = async () => {
    // Recherche maintenant gérée par l'API
    await refreshTemplates();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const templateVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.02,
      y: -4,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="min-h-screen bg-gray-50"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header avec animations */}
      <motion.header 
        className="bg-white border-b border-gray-200 px-6 py-4"
        variants={itemVariants}
      >
        <div className="flex items-center justify-between">
          <div>
            <motion.h1 
              className="text-h1 text-gray-900"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              Gestion des Templates
            </motion.h1>
            <motion.p 
              className="text-caption"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Administrez vos 3,247 templates de CV
            </motion.p>
          </div>
          <div className="flex items-center space-x-3">
            <motion.button 
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scanTemplates}
              disabled={isDetecting}
            >
              <Search className={`h-4 w-4 ${isDetecting ? 'animate-pulse' : ''}`} />
              <span>{isDetecting ? 'Scan...' : 'Scanner'}</span>
            </motion.button>
            <motion.button 
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={autoSync}
              disabled={isDetecting}
            >
              <RefreshCw className={`h-4 w-4 ${isDetecting ? 'animate-spin' : ''}`} />
              <span>{isDetecting ? 'Sync...' : 'Auto-Sync'}</span>
            </motion.button>
            <motion.button 
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNavigateToTemplateManager}
            >
              <Plus className="h-4 w-4" />
              <span>Nouveau template</span>
            </motion.button>
          </div>
        </div>
      </motion.header>

      <main className="p-6">
        {/* Indicateur de synchronisation automatique */}
        {syncStatus !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <RefreshCw className={`h-4 w-4 ${isDetecting ? 'animate-spin' : ''}`} />
                <span className="text-sm text-blue-700">
                  {syncStatus === 'detecting' && '🔍 Détection des templates...'}
                  {syncStatus === 'syncing' && '🔄 Synchronisation en cours...'}
                  {syncStatus === 'completed' && '✅ Synchronisation terminée'}
                  {syncStatus === 'error' && '❌ Erreur de synchronisation'}
                </span>
              </div>
              {lastSyncTime && (
                <span className="text-xs text-blue-600">
                  Dernière sync: {lastSyncTime.toLocaleTimeString()}
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* Affichage des templates détectés */}
        {detectedTemplates && detectedTemplates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-green-800">
                📁 Templates détectés ({detectedTemplates.length})
              </h3>
              <button
                onClick={autoSync}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
              >
                Synchroniser
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {detectedTemplates.map((template) => (
                <div key={template.template_id} className="p-3 bg-white rounded-md border border-green-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-gray-900">{template.name}</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      {template.category}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600">
                    <p>ID: {template.template_id}</p>
                    <p>Industrie: {template.industry}</p>
                    <p>Style: {template.style}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Indicateur de chargement des templates */}
        {loading && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
          >
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
              <span className="text-sm text-yellow-700">Chargement des templates...</span>
            </div>
          </motion.div>
        )}

        {/* Debug: Afficher le nombre de templates chargés */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg"
        >
          <div className="flex items-center space-x-2">
            <span className="text-sm text-blue-700">
              📊 Templates chargés: {templates?.length || 0}
            </span>
            {templates && templates.length > 0 && (
              <span className="text-xs text-blue-600">
                (Dernier: {templates[0]?.name || templates[0]?.template_id})
              </span>
            )}
          </div>
        </motion.div>

        {/* Barre de filtres et recherche avec animations */}
        <motion.div 
          className="bg-white rounded-2xl p-6 border border-gray-200 mb-6"
          variants={itemVariants}
          whileHover={{ y: -1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Recherche */}
            <motion.div 
              className="flex-1 relative"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <motion.input
                type="text"
                placeholder="Rechercher un template..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                whileFocus={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              />
              {loading && (
                <motion.div
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full" />
                </motion.div>
              )}
            </motion.div>

            {/* Filtres */}
            <motion.div 
              className="flex gap-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {[
                { key: 'category', options: ['Toutes les catégories', 'Moderne', 'Classique', 'Créatif', 'Minimal'] },
                { key: 'industry', options: ['Toutes les industries', 'Tech', 'Finance', 'Santé', 'Éducation'] },
                { key: 'sort', options: ['Plus récents', 'Plus populaires', 'Mieux notés', 'Plus téléchargés'] }
              ].map((filter, index) => (
                <motion.select
                  key={filter.key}
                  className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  value={filters[filter.key as keyof typeof filters]}
                  onChange={(e) => setFilters({...filters, [filter.key]: e.target.value})}
                  whileFocus={{ scale: 1.02 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                >
                  {filter.options.map((option, optionIndex) => (
                    <option key={`${filter.key}-${option}`} value={optionIndex === 0 ? '' : option.toLowerCase()}>
                      {option}
                    </option>
                  ))}
                </motion.select>
              ))}
            </motion.div>

            {/* Mode d'affichage */}
            <motion.div 
              className="flex border border-gray-300 rounded-xl overflow-hidden"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <motion.button
                onClick={() => setViewMode('grid')}
                className={`p-3 ${
                  viewMode === 'grid' 
                    ? 'bg-primary-500 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Grid className="h-4 w-4" />
              </motion.button>
              <motion.button
                onClick={() => setViewMode('list')}
                className={`p-3 ${
                  viewMode === 'list' 
                    ? 'bg-primary-500 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <List className="h-4 w-4" />
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        {/* Grille des templates avec animations */}
        <motion.div 
          className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
          variants={itemVariants}
          whileHover={{ y: -1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence mode="wait">
            {viewMode === 'grid' ? (
              <motion.div 
                key="grid"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                {templates && templates.map((template, index) => (
                  <motion.div
                    key={template.template_id}
                    className="group cursor-pointer"
                    variants={templateVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    custom={index}
                    onClick={() => handleViewTemplate(template)}
                    onDoubleClick={() => handleSelectTemplate(template.template_id)}
                  >
                    <motion.div 
                      className="aspect-[3/4] relative overflow-hidden rounded-xl bg-gray-100"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                       <TemplatePreview 
                         templateName={template.component_name || template.name || template.template_id}
                         templateApiId={template.template_id}
                         userData={sampleUserData}
                         scale={0.5}
                         className="w-full h-full aspect-[3/4]"
                       />
                      <motion.div 
                        className="absolute top-3 right-3"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <span className="px-2 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-medium text-gray-700">
                          {template.category}
                        </span>
                      </motion.div>
                      <motion.div 
                        className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"
                        whileHover={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                      />
                      {selectedTemplates.includes(template.template_id) && (
                        <motion.div
                          className="absolute inset-0 bg-primary-500/20 flex items-center justify-center"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <motion.div
                            className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <span className="text-white text-sm">✓</span>
                          </motion.div>
                        </motion.div>
                      )}
                    </motion.div>
                    
                    <motion.div 
                      className="mt-4"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 + 0.2 }}
                    >
                      <h3 className="font-semibold text-gray-900 mb-2">{template.name || template.template_id}</h3>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {(template.tags || []).slice(0, 2).map((tag, tagIndex) => (
                          <motion.span
                            key={tag}
                            className="px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded-md"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2, delay: tagIndex * 0.1 }}
                          >
                            {tag}
                          </motion.span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Download className="h-4 w-4" />
                          <span>{template.downloads}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span>{template.rating}</span>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div 
                key="list"
                className="divide-y divide-gray-200"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                {templates && templates.map((template, index) => (
                  <motion.div
                    key={template.template_id}
                    className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleViewTemplate(template)}
                  >
                    <div className="flex items-center space-x-4">
                      <motion.div 
                        className="w-16 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.2 }}
                      >
                         <TemplatePreview 
                           templateName={template.component_name || template.name || template.template_id}
                           templateApiId={template.template_id}
                           userData={sampleUserData}
                           scale={1.5}
                           className="w-full h-full"
                         />
                      </motion.div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1">{template.name || template.template_id}</h3>
                        <p className="text-sm text-gray-500 mb-2">{template.category || 'N/A'} • {template.industry || 'N/A'}</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(template.tags || []).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded-md"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Download className="h-4 w-4" />
                            <span>{template.downloads} téléchargements</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span>{template.rating}/5</span>
                          </div>
                          <span>{template.createdAt}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <motion.button 
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => handleViewTemplate(template)}
                        >
                          <Eye className="h-4 w-4" />
                        </motion.button>
                        <motion.button 
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Pagination avec animations */}
        <motion.div 
          className="flex items-center justify-between mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <p className="text-sm text-gray-500">
            Affichage de 1 à 20 sur 3,247 templates
          </p>
          <div className="flex items-center space-x-2">
            {['Précédent', '1', '2', '3', 'Suivant'].map((item, index) => (
              <motion.button
                key={item}
                className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                  item === '1' 
                    ? 'bg-primary-500 text-white' 
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.9 + index * 0.1 }}
              >
                {item}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Template Modal */}
        {selectedTemplate && (
          <TemplateModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedTemplate(null);
            }}
            template={selectedTemplate}
          />
        )}
      </main>
    </motion.div>
  );
};
